import { useState, useEffect, useCallback } from 'react'
import { useNearbyListings } from '../useNearbyListings'
import { useMapSpot } from '../../context/MapSpotContext'

/**
 * useHomePageListings - Ephemeral listings layer state for HomePageMap
 * (toggle, nearby fetch, empty-state dismissal, post-listing spot-mode entry)
 * @atomic @idempotent
 */
export function useHomePageListings({ mapRef, userLocation, mapCenter, user }) {
  const [showListings, setShowListings] = useState(false)

  // Priority: GPS location > current map center > initial map center fallback.
  const listingsLat = showListings ? (userLocation?.lat ?? mapRef.current?.getCenter()?.lat ?? mapCenter?.[0]) : null
  const listingsLng = showListings ? (userLocation?.lng ?? mapRef.current?.getCenter()?.lng ?? mapCenter?.[1]) : null
  const { listings, loading: listingsLoading, refresh: refreshListings } = useNearbyListings(listingsLat, listingsLng)

  // Show an empty-state pill when the layer is on but returns nothing (after load completes)
  const listingsEmpty = showListings && !listingsLoading && listings.length === 0
  // Dismissed flag resets automatically when the user turns the layer off and back on
  const [listingsEmptyDismissed, setListingsEmptyDismissed] = useState(false)
  useEffect(() => { if (!showListings) setListingsEmptyDismissed(false) }, [showListings])

  // Listing spot mode — activate SpotMode in 'listing' variant so the user
  // drops a pin for the pickup location and fills the form in the sidebar.
  const { activateSpotMode } = useMapSpot()
  const handlePostListingClick = useCallback(() => {
    if (!user) return // auth guard — should never be called unauthenticated
    setShowListings(true)
    const initialLoc = userLocation ?? (mapCenter ? { lat: mapCenter[0], lng: mapCenter[1] } : null)
    activateSpotMode(
      () => { refreshListings() },
      null,
      initialLoc,
      'listing'
    )
  }, [user, activateSpotMode, userLocation, mapCenter, refreshListings])

  return {
    showListings,
    setShowListings,
    listings,
    listingsLoading,
    refreshListings,
    listingsEmpty,
    listingsEmptyDismissed,
    setListingsEmptyDismissed,
    handlePostListingClick
  }
}
